/**
 * BaseRenderer - Abstract base class for all element renderers
 * Provides common functionality for rendering, updating, and transforming elements
 */

import Konva from 'konva';
import { IRenderer, Point, BoundingBox } from './types';
import { EditableElement } from '../editing/types';

/**
 * Abstract base class that all element renderers must extend
 */
export abstract class BaseRenderer implements IRenderer {
  protected _node: Konva.Group;
  protected element: EditableElement;
  
  constructor(element: EditableElement) {
    this.element = { ...element };
    
    // Create container group
    this._node = new Konva.Group({
      id: element.id,
      x: element.x,
      y: element.y,
      rotation: element.rotation || 0,
      draggable: false, // Dragging is managed externally
    });
    
    // Store actual element dimensions as custom attributes (not affected by shadows)
    this._node.setAttr('elementWidth', element.width);
    this._node.setAttr('elementHeight', element.height);
  }

  /**
   * Get the Konva group node
   */
  get node(): Konva.Group {
    return this._node;
  }

  /**
   * Get the element ID
   */
  get elementId(): string {
    return this.element.id;
  }

  /**
   * Render the element - must be implemented by subclasses
   */
  abstract render(): Konva.Node;

  /**
   * Update the element with new properties - must be implemented by subclasses
   */
  abstract update(updates: Partial<EditableElement>): void;

  /**
   * Destroy the renderer and its Konva nodes
   */
  destroy(): void {
    this._node.destroy();
  }

  /**
   * Get the element position
   */
  getPosition(): Point {
    return {
      x: this._node.x(),
      y: this._node.y(),
    };
  }

  /**
   * Set the element position
   */
  setPosition(x: number, y: number): void {
    this._node.x(x);
    this._node.y(y);
    this.element.x = x;
    this.element.y = y;
  }

  /**
   * Get the element rotation
   */
  getRotation(): number {
    return this._node.rotation();
  }

  /**
   * Set the element rotation
   */
  setRotation(angle: number): void {
    this._node.rotation(angle);
    this.element.rotation = angle;
  }

  /**
   * Get the element bounding box
   */
  getBounds(): BoundingBox {
    return {
      x: this.element.x,
      y: this.element.y,
      width: this.element.width,
      height: this.element.height,
    };
  }

  /**
   * Get the client rect (for transform calculations)
   */
  getClientRect(): BoundingBox {
    const rect = this._node.getClientRect();
    return {
      x: rect.x,
      y: rect.y,
      width: rect.width,
      height: rect.height,
    };
  }

  /**
   * Set dimensions
   */
  setDimensions(width: number, height: number): void {
    this.element.width = width;
    this.element.height = height;
    // Subclasses should override to update internal nodes
  }

  /**
   * Set draggable state
   */
  setDraggable(draggable: boolean): void {
    this._node.draggable(draggable);
  }

  /**
   * Check if element is selected
   */
  isSelected(): boolean {
    return this._node.hasName('selected');
  }

  /**
   * Mark element as selected
   */
  setSelected(selected: boolean): void {
    if (selected) {
      this._node.addName('selected');
    } else {
      this._node.removeName('selected');
    }
  }

  /**
   * Get the current element state
   */
  getElement(): EditableElement {
    return { ...this.element };
  }

  /**
   * Update element data
   */
  protected updateElementData(updates: Partial<EditableElement>): void {
    // Update position
    if (updates.x !== undefined) {
      this._node.x(updates.x);
      this.element.x = updates.x;
    }
    if (updates.y !== undefined) {
      this._node.y(updates.y);
      this.element.y = updates.y;
    }
    
    // Update rotation
    if (updates.rotation !== undefined) {
      this._node.rotation(updates.rotation);
      this.element.rotation = updates.rotation;
    }
    
    // Update dimensions and reset scale
    if (updates.width !== undefined) {
      this.element.width = updates.width;
      this._node.setAttr('elementWidth', updates.width);
    }
    if (updates.height !== undefined) {
      this.element.height = updates.height;
      this._node.setAttr('elementHeight', updates.height);
    }
    
    // Reset scale to 1 if dimensions were updated (we use width/height instead of scale)
    if (updates.width !== undefined || updates.height !== undefined) {
      this._node.scaleX(1);
      this._node.scaleY(1);
    }
    
    // Update data
    if (updates.data !== undefined) {
      this.element.data = { ...this.element.data, ...updates.data };
    }
  }

  /**
   * Utility: Create a Konva Rect with shadow
   */
  protected createRectWithShadow(options: {
    width: number;
    height: number;
    fill: string;
    cornerRadius?: number;
    shadowColor?: string;
    shadowBlur?: number;
    shadowOffsetX?: number;
    shadowOffsetY?: number;
    shadowOpacity?: number;
    stroke?: string;
    strokeWidth?: number;
  }): Konva.Rect {
    return new Konva.Rect({
      width: options.width,
      height: options.height,
      fill: options.fill,
      cornerRadius: options.cornerRadius || 0,
      shadowColor: options.shadowColor,
      shadowBlur: options.shadowBlur,
      shadowOffset: {
        x: options.shadowOffsetX || 0,
        y: options.shadowOffsetY || 0,
      },
      shadowOpacity: options.shadowOpacity,
      stroke: options.stroke,
      strokeWidth: options.strokeWidth,
    });
  }

  /**
   * Utility: Create a Konva Text node
   */
  protected createText(options: {
    text: string;
    width?: number;
    height?: number;
    fontSize?: number;
    fontFamily?: string;
    fontStyle?: string;
    fill?: string;
    align?: string;
    verticalAlign?: string;
    padding?: number;
    wrap?: string;
    lineHeight?: number;
    letterSpacing?: number;
  }): Konva.Text {
    return new Konva.Text({
      text: options.text,
      width: options.width,
      height: options.height,
      fontSize: options.fontSize || 14,
      fontFamily: options.fontFamily || 'Inter, sans-serif',
      fontStyle: options.fontStyle || 'normal',
      fill: options.fill || '#000000',
      align: options.align || 'left',
      verticalAlign: options.verticalAlign || 'top',
      padding: options.padding || 0,
      wrap: options.wrap || 'word',
      lineHeight: options.lineHeight,
      letterSpacing: options.letterSpacing,
    });
  }
}
